import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PageContainer from "../../../components/container/PageContainer";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import EditIcon from "@mui/icons-material/Edit";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";
import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";
import { AddOrEditShift } from "../../../services/asaiadmin";
/* =========================================================
   SHIFT TYPE CONFIGURATION
========================================================= */
const SHIFT_TYPES_BY_HOURS = {
  8: ["Payroll Flex", "Contract Flex", "Flex Apprentice"],
  10: ["Payroll CNC", "Contract CNC", "CNC Apprentice"],
};
/* =========================================================
   EMPTY FORM
========================================================= */
const EMPTY_FORM = {
  departmentIds: [],
  shiftNumber: "",
  shiftHours: "",
  shiftType: "",
  firstHalfInTime: "",
  firstHalfOutTime: "",
  morningTeaBreakInTime: "",
  morningTeaBreakOutTime: "",
  breakInTime: "",
  breakOutTime: "",
  eveningTeaBreakInTime: "",
  eveningTeaBreakOutTime: "",
  secondHalfInTime: "",
  secondHalfOutTime: "",
};
/* =========================================================
   FORMAT SHIFT NAME TIME
========================================================= */
const formatShiftNameTime = (time) => {
  if (!time) {
    return "";
  }
  const [hour, minute] = String(time).split(":");
  if (hour === undefined || minute === undefined) {
    return "";
  }
  let h = parseInt(hour, 10);
  if (Number.isNaN(h)) {
    return "";
  }
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) {
    h = 12;
  }
  return `${h}:${minute} ${suffix}`;
};
/* =========================================================
   FORMAT TIME
========================================================= */
const formatTime = (time) => {
  if (!time) {
    return "-";
  }
  const [hour, minute] = String(time).split(":");
  if (hour === undefined || minute === undefined) {
    return time;
  }
  let h = parseInt(hour, 10);
  if (Number.isNaN(h)) {
    return time;
  }
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) {
    h = 12;
  }
  return `${h}:${minute} ${suffix}`;
};
/* =========================================================
   API TIME HELPERS
========================================================= */
const toApiTime = (time) => {
  if (!time) {
    return null;
  }
  const value = String(time);
  return value.length === 5 ? `${value}:00` : value;
};
const fromApiTime = (time) => {
  if (!time) {
    return "";
  }
  const value = String(time);
  return value.length >= 5 ? value.substring(0, 5) : value;
};
/* =========================================================
   COMPARISON + API ERROR HELPERS
========================================================= */

const normalizeKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

// Shift rows must use an explicit department property. Do not fall back to
// row.id here, because that is the shift ID and can cause false duplicate matches.
const getDepartmentId = (item) =>
  item?.departmentId ??
  item?.DepartmentId ??
  item?.departmentID ??
  item?.DepartmentID ??
  item?.department_id ??
  item?.deptId ??
  item?.DeptId ??
  item?.deptID ??
  item?.DeptID ??
  "";

const getDepartmentIds = (item) => {
  let ids = item?.departmentIds ?? item?.DepartmentIds;

  if (!Array.isArray(ids) || ids.length === 0) {
    const rawJson = item?.departmentIdsJson ?? item?.DepartmentIdsJson;

    if (typeof rawJson === "string" && rawJson.trim()) {
      try {
        const parsed = JSON.parse(rawJson);
        if (Array.isArray(parsed)) {
          ids = parsed;
        }
      } catch {
        ids = [];
      }
    }
  }

  if (!Array.isArray(ids) || ids.length === 0) {
    const departmentId = getDepartmentId(item);
    ids = departmentId !== "" && departmentId != null ? [departmentId] : [];
  }

  return [
    ...new Set(
      ids
        .map(String)
        .map((id) => id.trim())
        .filter((id) => id && Number.isInteger(Number(id)) && Number(id) > 0),
    ),
  ];
};

// Department lookup options commonly expose their primary key as id/Id.
const getDepartmentOptionId = (item) =>
  item?.id ??
  item?.Id ??
  item?.ID ??
  item?.departmentId ??
  item?.DepartmentId ??
  "";

const getShiftRecordId = (item) =>
  item?.shiftId ??
  item?.ShiftId ??
  item?.shiftID ??
  item?.ShiftID ??
  item?.shiftMasterId ??
  item?.ShiftMasterId ??
  item?.id ??
  item?.Id ??
  item?.ID ??
  "";

const getDepartmentName = (item) =>
  item?.departmentName ??
  item?.DepartmentName ??
  item?.department_name ??
  item?.deptName ??
  item?.DeptName ??
  "";

// Prefer IDs; use department names only when at least one side lacks an ID.
const isSameDepartment = (item, departmentId, departmentName) => {
  const existingId = normalizeKey(getDepartmentId(item));
  const selectedId = normalizeKey(departmentId);

  if (existingId && selectedId) return existingId === selectedId;

  const existingName = normalizeKey(getDepartmentName(item));
  const selectedName = normalizeKey(departmentName);
  return Boolean(existingName && selectedName && existingName === selectedName);
};

const extractApiErrorMessage = (value, depth = 0) => {
  if (depth > 5 || value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value !== "object") return "";

  if (Array.isArray(value)) {
    for (const entry of value) {
      const message = extractApiErrorMessage(entry, depth + 1);
      if (message) return message;
    }
    return "";
  }

  for (const key of [
    "message",
    "Message",
    "errorMessage",
    "ErrorMessage",
    "detail",
    "Detail",
  ]) {
    const candidate = value[key];
    if (typeof candidate === "string" && candidate.trim())
      return candidate.trim();
  }

  for (const key of [
    "errors",
    "Errors",
    "validationErrors",
    "ValidationErrors",
    "error",
    "Error",
    "data",
    "Data",
    "result",
    "Result",
  ]) {
    const candidate = value[key];
    if (candidate == null || candidate === false || candidate === "") continue;
    const message = extractApiErrorMessage(candidate, depth + 1);
    if (message) return message;
  }

  for (const key of ["title", "Title"]) {
    const candidate = value[key];
    if (typeof candidate === "string" && candidate.trim())
      return candidate.trim();
  }

  // ASP.NET-style validation errors often use field names as object keys.
  for (const [key, candidate] of Object.entries(value)) {
    if (
      ["type", "status", "Status", "traceId", "TraceId", "instance"].includes(
        key,
      )
    )
      continue;
    const message = extractApiErrorMessage(candidate, depth + 1);
    if (message) return message;
  }

  return "";
};

const getFriendlyApiErrorMessage = (source, fallback) => {
  const message =
    typeof source === "string" ? source.trim() : extractApiErrorMessage(source);

  let rawDetails = message;

  try {
    if (source && typeof source === "object") {
      rawDetails += ` ${JSON.stringify(source)}`;
    }
  } catch {
    // Keep the extracted message if a response object cannot be serialized.
  }

  // The database uniqueness rule is department-specific.
  if (
    /UQ_ShiftMaster_ShiftDefinition|Violation of UNIQUE KEY constraint|duplicate key value|SQL exception number:?\s*2627/i.test(
      rawDetails,
    )
  ) {
    return "A shift with this code, hours, and type already exists in this department.";
  }

  // Do not show stack traces or request headers to users. Only apply the length
  // guard to raw string errors: a successful response object may be large even
  // when its Message says "Shift updated successfully.".
  if (
    /System\.Data\.SqlClient\.SqlException|DeveloperExceptionPageMiddleware|ExceptionMiddleware|\bHEADERS\s*={3,}|\bat SSWhite\./i.test(
      rawDetails,
    ) ||
    (typeof source === "string" && rawDetails.length > 500)
  ) {
    return (
      fallback ||
      "The server encountered an error. Please try again or contact support."
    );
  }

  return message || fallback || "";
};

const getRequestErrorMessage = (error, fallback) => {
  const responseMessage = getFriendlyApiErrorMessage(
    error?.response?.data,
    fallback,
  );
  if (responseMessage && responseMessage !== fallback) return responseMessage;

  return (
    getFriendlyApiErrorMessage(error, fallback) ||
    fallback ||
    "The request failed. Please try again."
  );
};

const isApiFailure = (result) => {
  if (!result || typeof result !== "object") return false;

  for (const key of [
    "success",
    "Success",
    "isSuccess",
    "IsSuccess",
    "succeeded",
    "Succeeded",
  ]) {
    if (result[key] === false) return true;
  }

  const status = result.status ?? result.Status;
  if (
    typeof status === "string" &&
    /^(error|failed|failure|false)$/i.test(status.trim())
  )
    return true;
  if (status === 0) return true;

  const message = extractApiErrorMessage(result);
  return /already\s+exists|duplicate|\bfailed\b|\bfailure\b|\bunable\b|\bcannot\b|\binvalid\b|not\s+found|\berror\b|\bexception\b/i.test(
    message,
  );
};

/* =========================================================
   MAP API RESPONSE
========================================================= */
const mapApiShift = (item) => ({
  id: getShiftRecordId(item),
  departmentId: getDepartmentId(item),
  departmentIds: getDepartmentIds(item),
  departmentIdsJson: item?.departmentIdsJson ?? item?.DepartmentIdsJson ?? "[]",
  shiftGroupId: item?.shiftGroupId ?? item?.ShiftGroupId ?? null,
  departmentName: getDepartmentName(item),
  shiftNumber: item?.shiftNumber ?? item?.ShiftNumber ?? "",
  shiftHours: item?.shiftHours ?? item?.ShiftHours ?? "",
  shiftType: item?.shiftType ?? item?.ShiftType ?? "",
  shiftName: item?.shiftName ?? item?.ShiftName ?? "",
  firstHalfInTime: fromApiTime(item?.firstHalfInTime ?? item?.FirstHalfInTime),
  firstHalfOutTime: fromApiTime(
    item?.firstHalfOutTime ?? item?.FirstHalfOutTime,
  ),
  morningTeaBreakInTime: fromApiTime(
    item?.morningTeaBreakInTime ?? item?.MorningTeaBreakInTime,
  ),
  morningTeaBreakOutTime: fromApiTime(
    item?.morningTeaBreakOutTime ?? item?.MorningTeaBreakOutTime,
  ),
  breakInTime: fromApiTime(item?.breakInTime ?? item?.BreakInTime),
  breakOutTime: fromApiTime(item?.breakOutTime ?? item?.BreakOutTime),
  eveningTeaBreakInTime: fromApiTime(
    item?.eveningTeaBreakInTime ?? item?.EveningTeaBreakInTime,
  ),
  eveningTeaBreakOutTime: fromApiTime(
    item?.eveningTeaBreakOutTime ?? item?.EveningTeaBreakOutTime,
  ),
  secondHalfInTime: fromApiTime(
    item?.secondHalfInTime ?? item?.SecondHalfInTime,
  ),
  secondHalfOutTime: fromApiTime(
    item?.secondHalfOutTime ?? item?.SecondHalfOutTime,
  ),
  shiftStartTime: fromApiTime(item?.shiftStartTime ?? item?.ShiftStartTime),
  shiftEndTime: fromApiTime(item?.shiftEndTime ?? item?.ShiftEndTime),
  isActive: Number(item?.isActive ?? item?.IsActive ?? 1) === 1,
});
/* =========================================================
   GENERATED SHIFT NAME
========================================================= */
const getShiftName = (item) => {
  const shiftNumber = item?.shiftNumber?.trim() || "";
  const startTime = formatShiftNameTime(item?.firstHalfInTime);
  const endTime = formatShiftNameTime(item?.secondHalfOutTime);
  if (!shiftNumber) {
    return "";
  }
  if (startTime && endTime) {
    return `Shift ${shiftNumber} (${startTime} to ${endTime})`;
  }
  if (startTime) {
    return `Shift ${shiftNumber} (${startTime} to --)`;
  }
  if (endTime) {
    return `Shift ${shiftNumber} (-- to ${endTime})`;
  }
  return `Shift ${shiftNumber}`;
};
/* =========================================================
   SHIFT DURATION
========================================================= */
const getShiftDurationLabel = (hours) => {
  if (!hours) {
    return "-";
  }
  return `${hours} Hours`;
};
/* =========================================================
   TIME TO MINUTES
========================================================= */
const timeToMinutes = (time) => {
  if (!time) {
    return null;
  }
  const [hours, minutes] = String(time).split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }
  return hours * 60 + minutes;
};
/* =========================================================
   TIME VALIDATION
========================================================= */
const validateTimeSequence = (values) => {
  const pairs = [
    {
      inLabel: "1st Half In Time",
      inValue: values.firstHalfInTime,
      outLabel: "1st Half Out Time",
      outValue: values.firstHalfOutTime,
      allowOvernight: false,
    },
    {
      inLabel: "1st Tea Break In Time",
      inValue: values.morningTeaBreakInTime,
      outLabel: "1st Tea Break Out Time",
      outValue: values.morningTeaBreakOutTime,
      allowOvernight: false,
    },
    {
      inLabel: "Lunch/Dinner Break In Time",
      inValue: values.breakInTime,
      outLabel: "Lunch/Dinner Break Out Time",
      outValue: values.breakOutTime,
      allowOvernight: false,
    },
    {
      inLabel: "2nd Tea Break In Time",
      inValue: values.eveningTeaBreakInTime,
      outLabel: "2nd Tea Break Out Time",
      outValue: values.eveningTeaBreakOutTime,
      allowOvernight: false,
    },
    {
      inLabel: "2nd Half In Time",
      inValue: values.secondHalfInTime,
      outLabel: "2nd Half Out Time",
      outValue: values.secondHalfOutTime,
      allowOvernight: true,
    },
  ];
  for (const pair of pairs) {
    if (!pair.inValue || !pair.outValue) {
      continue;
    }
    const inMinutes = timeToMinutes(pair.inValue);
    const outMinutes = timeToMinutes(pair.outValue);
    if (inMinutes === null || outMinutes === null) {
      continue;
    }
    if (pair.allowOvernight) {
      if (inMinutes === outMinutes) {
        return `${pair.outLabel} cannot be same as ${pair.inLabel}`;
      }
      continue;
    }
  }
  return null;
};
/* =========================================================
   COMPONENT
========================================================= */
const ShiftMaster = () => {
  const [departments, setDepartments] = useState([]);
  const [list, setList] = useState([]);
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [searchText, setSearchText] = useState({});
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const [currentStatus, setCurrentStatus] = useState(true);
  /* =======================================================
     BREADCRUMB
  ======================================================= */
  const BCrumb = [
    {
      to: "/",
      title: "ASAI Admin",
    },
    {
      title: "Shift Master",
    },
  ];
  /* =======================================================
     FETCH SHIFTS
  ======================================================= */
  const fetchShifts = async () => {
    try {
      const response = await AddOrEditShift({
        action: "GET",
      });
      const result = response?.data?.data ?? response?.data ?? [];
      const rows = Array.isArray(result) ? result : [];
      setList(rows.map(mapApiShift));
    } catch (error) {
      console.error("Failed to load shifts:", error);
      toast.error(
        getRequestErrorMessage(
          error,
          "Unable to load shifts. Please try again.",
        ),
      );
    }
  };
  /* =======================================================
     FETCH DEPARTMENTS
  ======================================================= */
  const fetchDepartments = async () => {
    try {
      const response = await AddOrEditShift({
        action: "GETDEPARTMENTS",
      });

      const result = response?.data?.data ?? response?.data ?? [];

      setDepartments(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error("Failed to load shift departments:", error);

      toast.error(
        getRequestErrorMessage(
          error,
          "Unable to load departments. Please try again.",
        ),
      );
    }
  };
  /* =======================================================
     INITIAL LOAD
  ======================================================= */
  useEffect(() => {
    fetchDepartments();
    fetchShifts();
  }, []);
  /* =======================================================
     VALIDATION
  ======================================================= */
  const validationSchema = Yup.object({
    departmentIds: Yup.array()
      .of(Yup.string().required())
      .min(1, "Select at least one department")
      .required("Department is required"),
    shiftNumber: Yup.string()
      .trim()
      .max(5, "Maximum 5 characters allowed")
      .matches(/^[A-Za-z0-9]+$/, "Only letters and numbers are allowed")
      .required("Shift number / code is required"),
    shiftHours: Yup.string().required("Shift hours are required"),
    shiftType: Yup.string()
      .required("Shift type is required")
      .test(
        "valid-shift-type",
        "Invalid shift type for selected shift hours",
        function (value) {
          const { shiftHours } = this.parent;
          if (!value || !shiftHours) {
            return true;
          }
          return SHIFT_TYPES_BY_HOURS[shiftHours]?.includes(value) ?? false;
        },
      ),
    firstHalfInTime: Yup.string().required("1st half in time is required"),
    firstHalfOutTime: Yup.string().required("1st half out time is required"),
    morningTeaBreakInTime: Yup.string(),
    morningTeaBreakOutTime: Yup.string(),
    breakInTime: Yup.string().required(
      "Lunch/Dinner break in time is required",
    ),
    breakOutTime: Yup.string().required(
      "Lunch/Dinner break out time is required",
    ),
    eveningTeaBreakInTime: Yup.string(),
    eveningTeaBreakOutTime: Yup.string(),
    secondHalfInTime: Yup.string().required("2nd half in time is required"),
    secondHalfOutTime: Yup.string().required("2nd half out time is required"),
  });
  /* =======================================================
     FORMIK
  ======================================================= */
  const formik = useFormik({
    initialValues: EMPTY_FORM,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        /* -----------------------------------------------
           DUPLICATE CHECK
        ------------------------------------------------ */
        const selectedDepartmentIds = values.departmentIds.map(String);

        const duplicateItem = list.find((item) => {
          // Never compare the record currently being edited against itself.
          if (editId != null && String(item.id) === String(editId)) {
            return false;
          }

          const sameShift =
            normalizeKey(item.shiftNumber) ===
              normalizeKey(values.shiftNumber) &&
            normalizeKey(item.shiftHours) === normalizeKey(values.shiftHours) &&
            normalizeKey(item.shiftType) === normalizeKey(values.shiftType);

          if (!sameShift) return false;

          return getDepartmentIds(item).some((id) =>
            selectedDepartmentIds.includes(String(id)),
          );
        });

        if (duplicateItem) {
          const conflictingIds = getDepartmentIds(duplicateItem).filter((id) =>
            selectedDepartmentIds.includes(String(id)),
          );

          const conflictingNames = conflictingIds.map((id) => {
            const department = departments.find(
              (d) => String(getDepartmentOptionId(d)) === String(id),
            );

            return department
              ? getDepartmentName(department)
              : `Department ID ${id}`;
          });

          console.warn("Shift duplicate detected", {
            editingShiftId: editId,
            conflictingShiftId: duplicateItem.id,
            selectedDepartmentIds,
            conflictingDepartmentIds: conflictingIds,
            conflictingDepartmentNames: conflictingNames,
          });

          toast.error(
            `Duplicate shift found for: ${conflictingNames.join(", ")}`,
          );

          return;
        }
        /* -----------------------------------------------
           TIME VALIDATION
        ------------------------------------------------ */
        const timeError = validateTimeSequence(values);
        if (timeError) {
          toast.error(timeError);
          return;
        }
        /* -----------------------------------------------
           GENERATED SHIFT NAME
        ------------------------------------------------ */
        const generatedShiftName = getShiftName(values);
        /* -----------------------------------------------
           API PAYLOAD
        ------------------------------------------------ */
        const payload = {
          action: editId ? "UPDATE" : "INSERT",
          id: editId || null,
          departmentId: Number(values.departmentIds[0]),
          departmentIds: values.departmentIds.map(Number),
          shiftNumber: values.shiftNumber?.trim(),
          shiftHours: Number(values.shiftHours),
          shiftType: values.shiftType?.trim(),
          shiftName: generatedShiftName,
          firstHalfInTime: toApiTime(values.firstHalfInTime),
          firstHalfOutTime: toApiTime(values.firstHalfOutTime),
          morningTeaBreakInTime: toApiTime(values.morningTeaBreakInTime),
          morningTeaBreakOutTime: toApiTime(values.morningTeaBreakOutTime),
          breakInTime: toApiTime(values.breakInTime),
          breakOutTime: toApiTime(values.breakOutTime),
          eveningTeaBreakInTime: toApiTime(values.eveningTeaBreakInTime),
          eveningTeaBreakOutTime: toApiTime(values.eveningTeaBreakOutTime),
          secondHalfInTime: toApiTime(values.secondHalfInTime),
          secondHalfOutTime: toApiTime(values.secondHalfOutTime),
          shiftStartTime: toApiTime(values.firstHalfInTime),
          shiftEndTime: toApiTime(values.secondHalfOutTime),
          isActive: currentStatus ? 1 : 0,
        };
        /* -----------------------------------------------
           SAVE
        ------------------------------------------------ */
        const response = await AddOrEditShift(payload);
        const result =
          response?.data?.[0] ??
          response?.data?.data?.[0] ??
          response?.data?.data ??
          response?.data ??
          {};

        if (isApiFailure(result)) {
          toast.error(
            getFriendlyApiErrorMessage(
              result,
              `Unable to ${
                editId ? "update" : "add"
              } shift. Please verify the details and try again.`,
            ),
          );
          return;
        }

        const message = getFriendlyApiErrorMessage(result, "");

        await fetchShifts();
        toast.success(
          message ||
            (editId
              ? "Shift updated successfully"
              : "Shift added successfully"),
        );
        resetForm({
          values: EMPTY_FORM,
        });
        setCurrentStatus(true);
        setEditId(null);
        setPage(0);
      } catch (error) {
        console.error("Failed to save shift:", error);
        toast.error(
          getRequestErrorMessage(
            error,
            `Unable to ${
              editId ? "update" : "add"
            } shift. Please try again or contact support if the problem continues.`,
          ),
        );
      }
    },
  });
  /* =======================================================
     EDIT
  ======================================================= */
  const handleEdit = (item) => {
    setEditId(item.id);
    setCurrentStatus(item.isActive === true);

    const departmentIds = getDepartmentIds(item);

    formik.setValues({
      departmentIds,
      shiftNumber: item.shiftNumber || "",
      shiftHours: String(item.shiftHours || ""),
      shiftType: item.shiftType || "",

      firstHalfInTime: fromApiTime(item.firstHalfInTime),
      firstHalfOutTime: fromApiTime(item.firstHalfOutTime),
      morningTeaBreakInTime: fromApiTime(item.morningTeaBreakInTime),
      morningTeaBreakOutTime: fromApiTime(item.morningTeaBreakOutTime),
      breakInTime: fromApiTime(item.breakInTime),
      breakOutTime: fromApiTime(item.breakOutTime),
      eveningTeaBreakInTime: fromApiTime(item.eveningTeaBreakInTime),
      eveningTeaBreakOutTime: fromApiTime(item.eveningTeaBreakOutTime),
      secondHalfInTime: fromApiTime(item.secondHalfInTime),
      secondHalfOutTime: fromApiTime(item.secondHalfOutTime),
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  /* =======================================================
     RESET
  ======================================================= */
  const resetForm = () => {
    setEditId(null);
    setCurrentStatus(true);
    formik.resetForm({
      values: EMPTY_FORM,
    });
  };
  /* =======================================================
     STATUS UPDATE
     IMPORTANT:
     No DELETE API is used.
     The same AddOrEditShift API is used with
     action = UPDATE and IsActive = true/false.
  ======================================================= */
  const handleStatusChange = async (item, checked) => {
    try {
      const response = await AddOrEditShift({
        action: "UPDATE",
        id: item.id,
        isActive: checked ? 1 : 0,
      });
      const result =
        response?.data?.[0] ??
        response?.data?.data?.[0] ??
        response?.data?.data ??
        response?.data ??
        {};

      if (isApiFailure(result)) {
        toast.error(
          extractApiErrorMessage(result) ||
            "Unable to update shift status. Please try again.",
        );
        return;
      }

      await fetchShifts();
      toast.success(
        extractApiErrorMessage(result) ||
          (checked
            ? "Shift activated successfully."
            : "Shift deactivated successfully."),
      );
    } catch (error) {
      console.error("Failed to update shift status:", error);
      toast.error(
        getRequestErrorMessage(
          error,
          "Unable to update shift status. Please try again.",
        ),
      );
    }
  };
  /* =======================================================
     SORT
  ======================================================= */
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  /* =======================================================
     FILTER FIELDS
  ======================================================= */
  const filterFields = [
    "departmentName",
    "shiftName",
    "shiftNumber",
    "shiftHours",
    "shiftType",
    "firstHalfInTime",
    "firstHalfOutTime",
    "morningTeaBreakInTime",
    "morningTeaBreakOutTime",
    "breakInTime",
    "breakOutTime",
    "eveningTeaBreakInTime",
    "eveningTeaBreakOutTime",
    "secondHalfInTime",
    "secondHalfOutTime",
  ];
  /* =======================================================
     FILTER
  ======================================================= */
  const filteredList = useMemo(() => {
    return list.filter((item) => {
      const searchableText = [
        item.departmentName,
        getShiftName(item),
        item.shiftNumber,
        item.shiftHours,
        item.shiftType,
        item.firstHalfInTime,
        item.firstHalfOutTime,
        item.morningTeaBreakInTime,
        item.morningTeaBreakOutTime,
        item.breakInTime,
        item.breakOutTime,
        item.eveningTeaBreakInTime,
        item.eveningTeaBreakOutTime,
        item.secondHalfInTime,
        item.secondHalfOutTime,
      ]
        .join(" ")
        .toLowerCase();
      const globalMatch = searchableText.includes(globalSearch.toLowerCase());
      const columnMatch = Object.keys(searchText).every((key) => {
        if (!searchText[key]) {
          return true;
        }
        let value = item[key];
        if (key === "shiftName") {
          value = getShiftName(item);
        }
        value = value?.toString().toLowerCase() || "";
        return value.includes(searchText[key].toLowerCase());
      });
      return globalMatch && columnMatch;
    });
  }, [list, globalSearch, searchText]);
  /* =======================================================
     SORTED LIST
  ======================================================= */
  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      if (!orderBy) {
        return 0;
      }
      let valueA;
      let valueB;
      if (orderBy === "shiftName") {
        valueA = getShiftName(a);
        valueB = getShiftName(b);
      } else {
        valueA = a[orderBy];
        valueB = b[orderBy];
      }
      if (typeof valueA === "string") {
        valueA = valueA.toLowerCase();
      }
      if (typeof valueB === "string") {
        valueB = valueB.toLowerCase();
      }
      if (valueA < valueB) {
        return order === "asc" ? -1 : 1;
      }
      if (valueA > valueB) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredList, orderBy, order]);
  /* =======================================================
     PAGINATION SAFETY
  ======================================================= */
  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredList.length / rowsPerPage) - 1,
    );
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredList.length, rowsPerPage, page]);
  /* =======================================================
     PAGINATION
  ======================================================= */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  /* =======================================================
     RESET FILTERS
  ======================================================= */
  const handleResetFilters = () => {
    setGlobalSearch("");
    setSearchText({});
    setShowAllFilters(false);
    setPage(0);
  };
  /* =======================================================
     TABLE HEADER
  ======================================================= */
  const renderHeader = (label, field, cellSx = {}) => (
    <TableCell sx={cellSx}>
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{ color: "text.primary" }}
      >
        <TableSortLabel
          active={orderBy === field}
          direction={orderBy === field ? order : "asc"}
          onClick={() => handleSort(field)}
        >
          {label}
        </TableSortLabel>
      </Box>
    </TableCell>
  );
  /* =======================================================
     TIME FIELD
  ======================================================= */
  const renderTimeField = (name, label) => (
    <Grid item lg={2.4} md={4} sm={6} xs={12}>
      <CustomFormLabel>{label}</CustomFormLabel>
      <CustomTextField
        type="time"
        name={name}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        fullWidth
        size="small"
        InputLabelProps={{
          shrink: true,
        }}
        error={formik.touched[name] && Boolean(formik.errors[name])}
        helperText={formik.touched[name] && formik.errors[name]}
      />
    </Grid>
  );
  /* =======================================================
     LIVE SHIFT NAME
  ======================================================= */
  const liveShiftName = getShiftName(formik.values);
  /* =======================================================
     RENDER
  ======================================================= */
  return (
    <PageContainer title="Shift Master">
      <Breadcrumb title="Shift Master" items={BCrumb} />
      <Box>
        {/* =================================================
            ADD / EDIT SHIFT
        ================================================= */}
        <Card
          sx={{
            mb: 2,
            p: 0,
            overflow: "hidden",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            px={2}
            py={1.5}
            sx={{
              backgroundColor: "primary.light",
              color: "primary.main",
            }}
          >
            <FeatherIcon icon="clock" width="18" />
            <Box
              sx={{
                ml: 1,
              }}
            >
              {editId ? "Edit Shift" : "Add Shift"}
            </Box>
          </Box>
          <Box component="form" onSubmit={formik.handleSubmit}>
            <CardContent
              sx={{
                padding: "10px 24px",
              }}
            >
              {/* =================================================
                  SHIFT INFORMATION - SINGLE ROW
              ================================================= */}
              <Grid container spacing={2} alignItems="flex-start">
                {/* DEPARTMENT */}
                <Grid item lg={2.4} md={6} sm={12} xs={12}>
                  <CustomFormLabel>Departments</CustomFormLabel>

                  <CustomTextField
                    select
                    name="departmentIds"
                    value={formik.values.departmentIds}
                    onChange={(event) => {
                      const value = event.target.value;

                      formik.setFieldValue(
                        "departmentIds",
                        typeof value === "string" ? value.split(",") : value,
                      );
                    }}
                    onBlur={formik.handleBlur}
                    fullWidth
                    size="small"
                    error={
                      formik.touched.departmentIds &&
                      Boolean(formik.errors.departmentIds)
                    }
                    helperText={
                      formik.touched.departmentIds &&
                      formik.errors.departmentIds
                    }
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) =>
                        departments
                          .filter((department) =>
                            selected.includes(
                              String(getDepartmentOptionId(department)),
                            ),
                          )
                          .map((department) => getDepartmentName(department))
                          .filter(Boolean)
                          .join(", "),
                      MenuProps: {
                        PaperProps: {
                          sx: { maxHeight: 300 },
                        },
                      },
                    }}
                  >
                    {departments.map((department) => {
                      const id = getDepartmentOptionId(department);
                      const name = getDepartmentName(department);

                      return (
                        <MenuItem key={id} value={String(id)}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </CustomTextField>
                </Grid>
                {/* SHIFT HOURS */}
                <Grid item lg={2.4} md={6} sm={12} xs={12}>
                  <CustomFormLabel>Shift Hours</CustomFormLabel>
                  <CustomTextField
                    select
                    name="shiftHours"
                    value={formik.values.shiftHours}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue("shiftHours", value);
                      const availableTypes = SHIFT_TYPES_BY_HOURS[value] || [];
                      if (!availableTypes.includes(formik.values.shiftType)) {
                        formik.setFieldValue("shiftType", "");
                      }
                    }}
                    onBlur={formik.handleBlur}
                    fullWidth
                    size="small"
                    error={
                      formik.touched.shiftHours &&
                      Boolean(formik.errors.shiftHours)
                    }
                    helperText={
                      formik.touched.shiftHours && formik.errors.shiftHours
                    }
                  >
                    <MenuItem value="">Select Shift Hours</MenuItem>
                    <MenuItem value="8">8 Hours</MenuItem>
                    <MenuItem value="10">10 Hours</MenuItem>
                  </CustomTextField>
                </Grid>
                {/* SHIFT TYPE */}
                <Grid item lg={2.4} md={6} sm={12} xs={12}>
                  <CustomFormLabel>Shift Type</CustomFormLabel>
                  <CustomTextField
                    select
                    name="shiftType"
                    value={formik.values.shiftType}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!formik.values.shiftHours}
                    fullWidth
                    size="small"
                    error={
                      formik.touched.shiftType &&
                      Boolean(formik.errors.shiftType)
                    }
                    helperText={
                      formik.touched.shiftType && formik.errors.shiftType
                    }
                  >
                    <MenuItem value="">Select Shift Type</MenuItem>
                    {(SHIFT_TYPES_BY_HOURS[formik.values.shiftHours] || []).map(
                      (type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ),
                    )}
                  </CustomTextField>
                </Grid>
                {/* SHIFT NUMBER */}
                <Grid item lg={2.4} md={6} sm={12} xs={12}>
                  <CustomFormLabel>Shift No. / Code</CustomFormLabel>
                  <CustomTextField
                    name="shiftNumber"
                    value={formik.values.shiftNumber}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^a-zA-Z0-9]/g, "")
                        .slice(0, 5);
                      formik.setFieldValue("shiftNumber", value);
                    }}
                    onBlur={formik.handleBlur}
                    placeholder="e.g. 1 / A"
                    fullWidth
                    size="small"
                    error={
                      formik.touched.shiftNumber &&
                      Boolean(formik.errors.shiftNumber)
                    }
                    helperText={
                      formik.touched.shiftNumber && formik.errors.shiftNumber
                    }
                  />
                </Grid>
                {/* SHIFT NAME */}
                <Grid item lg={2.4} md={6} sm={12} xs={12}>
                  <CustomFormLabel>Shift Name</CustomFormLabel>
                  <CustomTextField
                    value={liveShiftName}
                    fullWidth
                    size="small"
                    disabled
                    placeholder="Shift Name Preview"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FeatherIcon icon="clock" width="16" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <Divider
                sx={{
                  my: 2,
                }}
              />
              <Grid container spacing={2} alignItems="flex-start">
                {renderTimeField("firstHalfInTime", "1st Half In Time")}
                {renderTimeField("firstHalfOutTime", "1st Half Out Time")}
                {renderTimeField(
                  "morningTeaBreakInTime",
                  "1st Tea Break In Time (Optional)",
                )}
                {renderTimeField(
                  "morningTeaBreakOutTime",
                  "1st Tea Break Out Time (Optional)",
                )}
                {renderTimeField("breakInTime", "Lunch/Dinner Break In Time")}
                {renderTimeField("breakOutTime", "Lunch/Dinner Break Out Time")}
                {renderTimeField(
                  "eveningTeaBreakInTime",
                  "2nd Tea Break In Time (Optional)",
                )}
                {renderTimeField(
                  "eveningTeaBreakOutTime",
                  "2nd Tea Break Out Time (Optional)",
                )}
                {renderTimeField("secondHalfInTime", "2nd Half In Time")}
                {renderTimeField("secondHalfOutTime", "2nd Half Out Time")}
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    mt: 1,
                  }}
                >
                  <Button variant="contained" type="submit">
                    {editId ? "Update" : "Add"}
                  </Button>
                  <Button
                    sx={{ ml: 2 }}
                    variant="outlined"
                    type="button"
                    onClick={resetForm}
                  >
                    {editId ? "Cancel" : "Reset"}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Box>
        </Card>
        {/* =================================================
            EXISTING SHIFTS
        ================================================= */}
        <Card
          sx={{
            mt: 2,
            p: 0,
            overflow: "hidden",
          }}
        >
          {/* LIST HEADER */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1.5}
            px={2}
            py={1.5}
            sx={{
              backgroundColor: "primary.light",
              color: "primary.main",
            }}
          >
            {/* TITLE */}
            <Box display="flex" alignItems="center">
              <FeatherIcon icon="alert-circle" width="18" />
              <Box
                sx={{
                  ml: 1,
                }}
              >
                Existing Shifts
              </Box>
            </Box>
            {/* SEARCH + FILTER */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              flexWrap="wrap"
              gap={1}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <TextField
                size="small"
                placeholder="Search..."
                value={globalSearch}
                onFocus={() => {
                  setShowAllFilters(false);
                  setSearchText({});
                }}
                onChange={(e) => {
                  setGlobalSearch(e.target.value);
                  setPage(0);
                }}
                sx={{
                  width: { xs: "100%", sm: 220 },
                  minWidth: { sm: 180 },
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{
                        mr: 1,
                      }}
                    >
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              {globalSearch && (
                <CloseIcon
                  sx={{
                    cursor: "pointer",
                    ml: 1,
                  }}
                  onClick={() => {
                    setGlobalSearch("");
                    setPage(0);
                  }}
                />
              )}
              <FilterAltIcon
                sx={{
                  cursor: "pointer",
                  ml: 1,
                  color: showAllFilters ? "primary.main" : "text.primary",
                  transition: "0.2s",
                }}
                onClick={() => setShowAllFilters(!showAllFilters)}
              />
              <Button
                variant="outlined"
                size="small"
                sx={{
                  ml: 1,
                  height: 32,
                  whiteSpace: "nowrap",
                }}
                onClick={handleResetFilters}
              >
                Reset Filter
              </Button>
            </Box>
          </Box>
          <CardContent sx={{ p: 2 }}>
            {filteredList.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 1 }}>
                {globalSearch.trim() ||
                Object.values(searchText).some((value) =>
                  String(value ?? "").trim(),
                )
                  ? "No matching shift records found."
                  : "No Shifts Found"}
              </Typography>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                  }}
                >
                  <Table
                    sx={{
                      minWidth: 1750,
                      "& .MuiTableCell-root": {
                        whiteSpace: "nowrap",
                        verticalAlign: "middle",
                      },
                    }}
                  >
                    <TableHead>
                      {/* MAIN HEADER */}
                      <TableRow
                        sx={{
                          backgroundColor: "#FAFBFB",
                          "& .MuiTableCell-root": {
                            position: "sticky",
                            top: 0,
                            zIndex: 4,
                            backgroundColor: "#FAFBFB",
                          },
                        }}
                      >
                        {renderHeader("Department", "departmentName", {
                          width: 280,
                          minWidth: 280,
                          maxWidth: 280,
                        })}
                        {renderHeader("Shift Name", "shiftName")}
                        {renderHeader("Shift No.", "shiftNumber")}
                        {renderHeader("Shift Hours", "shiftHours")}
                        {renderHeader("Shift Type", "shiftType")}
                        {renderHeader("1st Half In", "firstHalfInTime")}
                        {renderHeader("1st Half Out", "firstHalfOutTime")}
                        {renderHeader("1st Tea In", "morningTeaBreakInTime")}
                        {renderHeader("1st Tea Out", "morningTeaBreakOutTime")}
                        {renderHeader("Lunch/Dinner In", "breakInTime")}
                        {renderHeader("Lunch/Dinner Out", "breakOutTime")}
                        {renderHeader("2nd Tea In", "eveningTeaBreakInTime")}
                        {renderHeader("2nd Tea Out", "eveningTeaBreakOutTime")}
                        {renderHeader("2nd Half In", "secondHalfInTime")}
                        {renderHeader("2nd Half Out", "secondHalfOutTime")}
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                      {/* FILTER ROW */}
                      {showAllFilters && (
                        <TableRow
                          sx={{
                            backgroundColor: "#FFFFFF",
                            "& .MuiTableCell-root": {
                              padding: "6px 8px",
                              height: 42,
                              verticalAlign: "middle",
                            },
                          }}
                        >
                          {filterFields.map((field) => (
                            <TableCell key={field}>
                              <TextField
                                size="small"
                                fullWidth
                                value={searchText[field] || ""}
                                onChange={(e) => {
                                  setSearchText({
                                    ...searchText,
                                    [field]: e.target.value,
                                  });
                                  setPage(0);
                                }}
                                placeholder="Search..."
                                sx={{
                                  minWidth: 90,
                                  "& .MuiInputBase-root": {
                                    height: 32,
                                    fontSize: 13,
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "6px 8px",
                                  },
                                }}
                                InputProps={{
                                  endAdornment: searchText[field] ? (
                                    <InputAdornment position="end">
                                      <CloseIcon
                                        fontSize="small"
                                        sx={{
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          setSearchText({
                                            ...searchText,
                                            [field]: "",
                                          })
                                        }
                                      />
                                    </InputAdornment>
                                  ) : null,
                                }}
                              />
                            </TableCell>
                          ))}
                          {/* Action column has no filter */}
                          <TableCell />
                        </TableRow>
                      )}
                    </TableHead>
                    <TableBody>
                      {sortedList.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={16} align="center">
                            No data found
                          </TableCell>
                        </TableRow>
                      )}
                      {(rowsPerPage > 0
                        ? sortedList.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                        : sortedList
                      ).map((item) => (
                        <TableRow key={item.id} hover>
                          {/* DEPARTMENT WITH TOOLTIP */}
                          <TableCell
                            sx={{
                              width: 280,
                              minWidth: 280,
                              maxWidth: 280,
                              overflow: "hidden",
                            }}
                          >
                            <Tooltip
                              title={item.departmentName || "-"}
                              arrow
                              placement="top"
                              enterDelay={300}
                            >
                              <Typography
                                component="div"
                                noWrap
                                sx={{
                                  display: "block",
                                  width: "100%",
                                  maxWidth: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  cursor: "help",
                                }}
                              >
                                {item.departmentName || "-"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          {/* SHIFT NAME */}
                          <TableCell>
                            <Typography
                              sx={{
                                whiteSpace: "nowrap",
                              }}
                            >
                              {getShiftName(item) || "-"}
                            </Typography>
                          </TableCell>
                          {/* SHIFT NUMBER */}
                          <TableCell>
                            <Typography>{item.shiftNumber || "-"}</Typography>
                          </TableCell>
                          {/* SHIFT HOURS */}
                          <TableCell>
                            <Typography>
                              {getShiftDurationLabel(item.shiftHours)}
                            </Typography>
                          </TableCell>
                          {/* SHIFT TYPE */}
                          <TableCell>{item.shiftType || "-"}</TableCell>
                          {/* FIRST HALF IN */}
                          <TableCell>
                            {formatTime(item.firstHalfInTime)}
                          </TableCell>
                          {/* FIRST HALF OUT */}
                          <TableCell>
                            {formatTime(item.firstHalfOutTime)}
                          </TableCell>
                          {/* 1ST TEA IN */}
                          <TableCell>
                            {formatTime(item.morningTeaBreakInTime)}
                          </TableCell>
                          {/* 1ST TEA OUT */}
                          <TableCell>
                            {formatTime(item.morningTeaBreakOutTime)}
                          </TableCell>
                          {/* LUNCH/DINNER BREAK IN */}
                          <TableCell>{formatTime(item.breakInTime)}</TableCell>
                          {/* LUNCH/DINNER BREAK OUT */}
                          <TableCell>{formatTime(item.breakOutTime)}</TableCell>
                          {/* 2ND TEA IN */}
                          <TableCell>
                            {formatTime(item.eveningTeaBreakInTime)}
                          </TableCell>
                          {/* 2ND TEA OUT */}
                          <TableCell>
                            {formatTime(item.eveningTeaBreakOutTime)}
                          </TableCell>
                          {/* SECOND HALF IN */}
                          <TableCell>
                            {formatTime(item.secondHalfInTime)}
                          </TableCell>
                          {/* SECOND HALF OUT */}
                          <TableCell>
                            {formatTime(item.secondHalfOutTime)}
                          </TableCell>
                          {/* ACTION */}
                          <TableCell align="center">
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              gap={1}
                            >
                              {/* STATUS SLIDER */}
                              <Tooltip
                                title={
                                  item.isActive !== false
                                    ? "Active"
                                    : "Inactive"
                                }
                              >
                                <Switch
                                  checked={item.isActive !== false}
                                  size="small"
                                  onChange={(e) =>
                                    handleStatusChange(item, e.target.checked)
                                  }
                                />
                              </Tooltip>
                              {/* EDIT */}
                              <Tooltip title="Edit">
                                <EditIcon
                                  sx={{
                                    cursor: "pointer",
                                    color: "primary.main",
                                  }}
                                  onClick={() => handleEdit(item)}
                                />
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredList.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  showFirstButton
                  showLastButton
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </PageContainer>
  );
};
export default ShiftMaster;